import classnames from 'classnames'
import React, { useCallback, useMemo } from 'react'
import GroupAddIcon from '@material-symbols/svg-300/outlined/group_add.svg'
import QuestionMarkIcon from '@material-symbols/svg-300/outlined/question_mark.svg'
import StarIcon from '@material-symbols/svg-300/outlined/stars.svg'
import GiftIcon from '@material-symbols/svg-300/outlined/featured_seasonal_and_gifts.svg'
import ScheduleSendIcon from '@material-symbols/svg-300/outlined/schedule_send.svg'
import CelebrationIcon from '@material-symbols/svg-300/outlined/celebration.svg'
import WorkspacePremiumIcon from '@material-symbols/svg-300/outlined/workspace_premium.svg'
import WarningIcon from '@material-symbols/svg-300/outlined/warning.svg'
import Comment from '@material-symbols/svg-300/outlined/chat.svg'
import EventIcon from '@material-symbols/svg-300/outlined/event.svg'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Notification } from '../../models/Notification'
import { useGroup, useLearningItem, useUser } from '../../state/entities'
import useMarkNotificationsRead from '../../state/mutations/useMarkNotificationsRead'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import Button from '../basics/Button/Button'
import GroupSlug from '../slugs/GroupSlug/GroupSlug'
import UserSlug from '../slugs/UserSlug/UserSlug'
import StartCourseLink from '../StartCourseLink/StartCourseLink'
import styles from './notificationComponents.module.scss'

const notificationIcon = (Icon: React.FC) =>
  React.memo(({ notification }: { notification: Notification }) => {
    const markNotificationsRead = useMarkNotificationsRead()
    const handleMarkNotificationRead = useCallback(
      () => markNotificationsRead.mutate({ notificationUuids: [notification.uuid] }),
      [notification]
    )
    return (
      <div className={classnames(styles.NotificationIcon, { [styles.unread]: !notification.readAt })}>
        <Icon />
        {!notification.readAt && (
          <Button plain onClick={handleMarkNotificationRead} className={styles.markRead}></Button>
        )}
      </div>
    )
  })

export type NotificationComponents<T extends Notification> = {
  Icon: ({ notification }: { notification: T }) => React.ReactNode
  Message: ({ notification }: { notification: T }) => React.ReactNode
  Action: ({ notification }: { notification: T }) => React.ReactNode
}

const notificationComponents: {
  [k in Notification['type']]: NotificationComponents<Extract<Notification, { type: k }>>
} = {
  UserCreated: {
    Icon: notificationIcon(StarIcon),
    Message: React.memo(function UserCreatedNotificationMessage() {
      const { t } = useTranslation()
      const { user: currentUser } = useSession()
      return t('notifications.userCreated.message', { firstname: currentUser?.firstname })
    }),
    Action: React.memo(function UserCreatedNotificationAction({ notification }) {
      const { learningItem } = useLearningItem(notification.data.courseUuidIntroduction)
      return <StartCourseLink small fallbackToCoursePage learningItem={learningItem} />
    }),
  },
  UserAddedToGroup: {
    Icon: notificationIcon(GroupAddIcon),
    Message: React.memo(function UserAddedToGroupNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const { user } = useUser(notification.data.actionUser)
      const { group } = useGroup(notification.data.group)
      return (
        <Trans t={t} i18nKey="notifications.userAddedToGroup.message">
          <UserSlug user={user} />
          <GroupSlug group={group} />
        </Trans>
      )
    }),
    Action: React.memo(function UserAddedToGroupNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: notification.data.group })}>
          {t('group.cta view')}
        </Button>
      )
    }),
  },
  UserRemovedFromGroup: {
    Icon: notificationIcon(GroupAddIcon),
    Message: React.memo(function UserRemovedFromGroupNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const { user } = useUser(notification.data.actionUser)
      const { group } = useGroup(notification.data.group)
      return (
        <Trans t={t} i18nKey="notifications.userRemovedFromGroup.message">
          <UserSlug user={user} />
          <GroupSlug group={group} />
        </Trans>
      )
    }),
    Action: React.memo(function UserRemovedFromGroupNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: notification.data.group })}>
          {t('group.cta view')}
        </Button>
      )
    }),
  },
  UserAddedAsManagerToGroup: {
    Icon: notificationIcon(GroupAddIcon),
    Message: React.memo(function UserAddedAsManagerToGroupNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const { user } = useUser(notification.data.actionUser)
      const { group } = useGroup(notification.data.group)
      return (
        <Trans t={t} i18nKey="notifications.userAddedAsManagerToGroup.message">
          <UserSlug user={user} />
          <GroupSlug group={group} />
        </Trans>
      )
    }),
    Action: React.memo(function UserAddedAsManagerToGroupNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: notification.data.group })}>
          {t('group.cta view')}
        </Button>
      )
    }),
  },
  UserRemovedAsManagerFromGroup: {
    Icon: notificationIcon(GroupAddIcon),
    Message: React.memo(function UserRemovedAsManagerFromGroupNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const { user } = useUser(notification.data.actionUser)
      const { group } = useGroup(notification.data.group)
      return (
        <Trans t={t} i18nKey="notifications.userRemovedAsManagerFromGroup.message">
          <UserSlug user={user} />
          <GroupSlug group={group} />
        </Trans>
      )
    }),
    Action: React.memo(function UserRemovedAsManagerFromGroupNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: notification.data.group })}>
          {t('group.cta view')}
        </Button>
      )
    }),
  },
  UserAddedLearningSuggestion: {
    Icon: notificationIcon(GiftIcon),
    Message: React.memo(function UserAddedLearningSuggestionNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { user } = useUser(notification.data.actionUser)
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.userAddedLearningSuggestionNotification.message">
          <UserSlug user={user} />
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function UserAddedLearningSuggestionNotificationAction({ notification }) {
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return <StartCourseLink small fallbackToCoursePage learningItem={learningItem} />
    }),
  },
  UserAddedLearningAssignment: {
    Icon: notificationIcon(ScheduleSendIcon),
    Message: React.memo(function UserAddedLearningAssignmentNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { user } = useUser(notification.data.actionUser)
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.userAddedLearningAssignmentNotification.message">
          <UserSlug user={user} />
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
          {{ dueDate: notification.data.dueDate }}
        </Trans>
      )
    }),
    Action: React.memo(function UserAddedLearningAssignmentNotificationAction({ notification }) {
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return <StartCourseLink small fallbackToCoursePage learningItem={learningItem} />
    }),
  },
  UserAddedLearningAssignmentWithdrawn: {
    Icon: notificationIcon(ScheduleSendIcon),
    Message: React.memo(function UserAddedLearningAssignmentWithdrawnNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { user } = useUser(notification.data.actionUser)
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.userAddedLearningAssignmentWithdrawnNotification.message">
          <UserSlug user={user} />
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function UserAddedLearningAssignmentWithdrawnNotificationAction() {
      return null
    }),
  },
  UserAddedLearningAssignmentLiveEvent: {
    Icon: notificationIcon(ScheduleSendIcon),
    Message: React.memo(function UserAddedLearningAssignmentNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { user } = useUser(notification.data.actionUser)
      const { learningItem } = useLearningItem(notification.data.learningItem)
      const event = useMemo(
        () =>
          learningItem.kind === 'scheduledCourse'
            ? learningItem.scheduledCourse.events.find(({ uuid }) => uuid === notification.data.event)
            : null,
        [learningItem, notification.data.booking]
      )
      if (!event) {
        return (
          <Trans
            t={t}
            i18nKey={`notifications.userAddedLearningAssignmentLiveEventNotification.message without date.${learningItem.type}`}
          >
            <UserSlug user={user} />
            <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
            {{ title: learningItem.title }}
          </Trans>
        )
      }
      return (
        <Trans
          t={t}
          i18nKey={`notifications.userAddedLearningAssignmentLiveEventNotification.message.${learningItem.type}`}
        >
          <UserSlug user={user} />
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
          {{ date: event.startDate }}
        </Trans>
      )
    }),
    Action: React.memo(function UserAddedLearningAssignmentNotificationAction({ notification }) {
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return <StartCourseLink small fallbackToCoursePage learningItem={learningItem} />
    }),
  },
  GroupAddedLearningSuggestion: {
    Icon: notificationIcon(GiftIcon),
    Message: React.memo(function GroupAddedLearningSuggestionNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { user } = useUser(notification.data.actionUser)
      const { group } = useGroup(notification.data.group)
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.groupAddedLearningSuggestionNotification.message">
          <GroupSlug group={group} />
          <UserSlug user={user} />
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function GroupAddedLearningSuggestionNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      const { group } = useGroup(notification.data.group)
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: group.uuid })}>
          {t('group.cta view')}
        </Button>
      )
    }),
  },
  GroupAddedLearningAssignment: {
    Icon: notificationIcon(ScheduleSendIcon),
    Message: React.memo(function GroupAddedLearningAssignmentNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { user } = useUser(notification.data.actionUser)
      const { group } = useGroup(notification.data.group)
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.groupAddedLearningAssignmentNotification.message">
          <GroupSlug group={group} />
          <UserSlug user={user} />
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
          {{ dueDate: notification.data.dueDate }}
        </Trans>
      )
    }),
    Action: React.memo(function GroupAddedLearningAssignmentNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      const { group } = useGroup(notification.data.group)
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: group.uuid })}>
          {t('group.cta view')}
        </Button>
      )
    }),
  },
  UserUpcomingEvent3Days: {
    Icon: notificationIcon(EventIcon),
    Message: React.memo(function CourseFinishedNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      const event = useMemo(
        () =>
          learningItem.kind === 'scheduledCourse'
            ? learningItem.scheduledCourse.events.find(({ uuid }) => uuid === notification.data.event)
            : null,
        [learningItem, notification]
      )
      const date = event?.dates[0]
      if (date?.meetingAddress) {
        return (
          <Trans t={t} i18nKey="notifications.userUpcomingEvent3DaysNotification.message">
            <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
            {{ title: learningItem.title }}
            {{ location: date.meetingAddress }}
            {{ date: notification.data.date }}
          </Trans>
        )
      }
      if (date?.meetingLink) {
        return (
          <Trans t={t} i18nKey="notifications.userUpcomingEvent3DaysNotification.message_virtual">
            <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
            <a target="_blank" href={date.meetingLink}></a>
            {{ title: learningItem.title }}
            {{ location: event?.dates[0].meetingLink }}
            {{ date: notification.data.date }}
          </Trans>
        )
      }
      return (
        <Trans t={t} i18nKey="notifications.userUpcomingEvent3DaysNotification.message_no_location">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
          {{ date: notification.data.date }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseFinishedNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.course.url({ uuid: notification.data.learningItem })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  UserUpcomingEvent: {
    Icon: notificationIcon(EventIcon),
    Message: React.memo(function CourseFinishedNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      const event = useMemo(
        () =>
          learningItem.kind === 'scheduledCourse'
            ? learningItem.scheduledCourse.events.find(({ uuid }) => uuid === notification.data.event)
            : null,
        [learningItem, notification]
      )
      const date = event?.dates[0]
      if (date?.meetingAddress) {
        return (
          <Trans t={t} i18nKey="notifications.userUpcomingEventNotification.message">
            <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
            {{ title: learningItem.title }}
            {{ location: date.meetingAddress }}
          </Trans>
        )
      }
      if (date?.meetingLink) {
        return (
          <Trans t={t} i18nKey="notifications.userUpcomingEventNotification.message_virtual">
            <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
            <a target="_blank" href={date.meetingLink}></a>
            {{ title: learningItem.title }}
            {{ location: event?.dates[0].meetingLink }}
          </Trans>
        )
      }
      return (
        <Trans t={t} i18nKey="notifications.userUpcomingEventNotification.message_no_location">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseFinishedNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.course.url({ uuid: notification.data.learningItem })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  CourseFinished: {
    Icon: notificationIcon(CelebrationIcon),
    Message: React.memo(function CourseFinishedNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)

      if (notification.data.withRatingLink) {
        return (
          <Trans t={ t } i18nKey="notifications.courseFinishedNotificationLink.message">
            <Link to={ routes.course.url({uuid: notification.data.learningItem}) }></Link>
            <a href={ notification.data.ratingLink
                .replace("{courseID}", notification.data.learningItem)
                .replace("{userID}", notification.data.userUuid) }
              target="_blank"
              rel="noopener noreferrer"
            ></a>
            { {title: learningItem.title} }
          </Trans>
        )
      }

      return (
        <Trans t={ t } i18nKey="notifications.courseFinishedNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseFinishedNotificationAction() {
      const routes = useRoutes()
      const { t } = useTranslation()
      const { user: currentUser } = useSession()
      return (
        <Button small component={Link} to={routes.manageUser.url({ uuid: currentUser!.uuid })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  UserLearningAssignmentDue7Days: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function CourseFinishedNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.userLearningAssignmentDue7DaysNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseFinishedNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.course.url({ uuid: notification.data.learningItem })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  UserLearningAssignmentDue3Days: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function CourseFinishedNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.userLearningAssignmentDue3DaysNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseFinishedNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.course.url({ uuid: notification.data.learningItem })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  UserLearningAssignmentOverdue: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function CourseFinishedNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.userLearningAssignmentOverdueNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseFinishedNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.course.url({ uuid: notification.data.learningItem })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  ManagerUserFinishedLearningAssignment: {
    Icon: notificationIcon(CelebrationIcon),
    Message: React.memo(function ManagerUserFinishedLearningAssignmentNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { user } = useUser(notification.data.actionUser)
      const { group } = useGroup(notification.data.group)
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.managerUserFinishedLearningAssignmentNotification.message">
          <UserSlug user={user} />
          <GroupSlug group={group} />
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function ManagerUserFinishedLearningAssignmentNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.manageUser.url({ uuid: notification.data.actionUser })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  ManagerLearningAssignmentOverdue: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function ManagerUserFinishedLearningAssignmentNotificationMessage({ notification }) {
      const { t } = useTranslation()
      // const routes = useRoutes()
      const { group } = useGroup(notification.data.groupUuid)
      return (
        <Trans t={t} i18nKey="notifications.managerLearningAssignmentOverdueNotification.message">
          <GroupSlug group={group} />
          {{ groupTitle: group.name }}
        </Trans>
      )
    }),
    Action: React.memo(function ManagerUserFinishedLearningAssignmentNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: notification.data.groupUuid })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  ManagerGroupFinishedLearningAssignment: {
    Icon: notificationIcon(CelebrationIcon),
    Message: React.memo(function ManagerGroupFinishedLearningAssignmentNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { group } = useGroup(notification.data.group)
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.managerGroupFinishedLearningAssignmentNotification.message">
          <GroupSlug group={group} />
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function ManagerGroupFinishedLearningAssignmentNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: notification.data.group })}>
          {t('group.cta view')}
        </Button>
      )
    }),
  },
  ManagerGroupLearningAssignmentOverdue: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function ManagerGroupFinishedLearningAssignmentNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { group } = useGroup(notification.data.group)
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.managerGroupLearningAssignmentOverdueNotification.message">
          <GroupSlug group={group} />
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
          {{ dueDate: notification.data.dueDate }}
          {{ completionRate: notification.data.completionRate }}
        </Trans>
      )
    }),
    Action: React.memo(function ManagerGroupFinishedLearningAssignmentNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: notification.data.group })}>
          {t('group.cta view')}
        </Button>
      )
    }),
  },
  CertificateAvailable: {
    Icon: notificationIcon(WorkspacePremiumIcon),
    Message: React.memo(function CertificateAvailableNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.certificateAvailableNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function CertificateAvailableNotificationAction() {
      const routes = useRoutes()
      const { t } = useTranslation()
      const { user: currentUser } = useSession()
      return (
        <Button small component={Link} to={routes.manageUser.url({ uuid: currentUser!.uuid })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  CertificateInvalidation60Days: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function CertificateAvailableNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.certificateInvalidation60DaysNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
          {{ validUntil: notification.data.validUntil }}
        </Trans>
      )
    }),
    Action: React.memo(function CertificateAvailableNotificationAction() {
      const routes = useRoutes()
      const { t } = useTranslation()
      const { user: currentUser } = useSession()
      return (
        <Button small component={Link} to={routes.manageUser.url({ uuid: currentUser!.uuid })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  CertificateInvalidation: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function CertificateAvailableNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.certificateInvalidationNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function CertificateAvailableNotificationAction() {
      const routes = useRoutes()
      const { t } = useTranslation()
      const { user: currentUser } = useSession()
      return (
        <Button small component={Link} to={routes.manageUser.url({ uuid: currentUser!.uuid })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  ManagerCertificateInvalidation60Days: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function CertificateAvailableNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      const { user } = useUser(notification.data.user)
      return (
        <Trans t={t} i18nKey="notifications.managerCertificateInvalidation60DaysNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          <UserSlug user={user} />
          {{ title: learningItem.title }}
          {{ validUntil: notification.data.validUntil }}
        </Trans>
      )
    }),
    Action: React.memo(function CertificateAvailableNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      const { group } = useGroup(notification.data.group)
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: group.uuid })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  ManagerCertificateInvalidation: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function CertificateAvailableNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      const { user } = useUser(notification.data.user)
      return (
        <Trans t={t} i18nKey="notifications.managerCertificateInvalidationNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          <UserSlug user={user} />
          {{ title: learningItem.title }}
          {{ validUntil: notification.data.validUntil }}
        </Trans>
      )
    }),
    Action: React.memo(function CertificateAvailableNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      const { group } = useGroup(notification.data.group)
      return (
        <Button small component={Link} to={routes.manageGroup.url({ uuid: group.uuid })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  ScheduledCourseChanged: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function ScheduledCourseChangedNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.scheduledCourseChangedNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseFinishedNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.course.url({ uuid: notification.data.learningItem })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  ScheduledCourseEventDeleted: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function ScheduledCourseEventDeletedNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      return (
        <Trans t={t} i18nKey="notifications.scheduledCourseEventDeletedNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItemUuid })}></Link>
          {{ title: notification.data.learningItemTitle }}
        </Trans>
      )
    }),
    Action: React.memo(function ScheduledCourseEventDeletedNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()

      return (
        <Button small component={Link} to={routes.course.url({ uuid: notification.data.learningItemUuid })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  CourseAttachmentAvailable: {
    Icon: notificationIcon(WarningIcon),
    Message: React.memo(function CourseAttachmentAvailableNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { learningItem } = useLearningItem(notification.data.learningItem)
      return (
        <Trans t={t} i18nKey="notifications.courseAttachmentAvailableNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItem })}></Link>
          {{ title: learningItem.title }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseFinishedNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button small component={Link} to={routes.course.url({ uuid: notification.data.learningItem })}>
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  TopicAddedInCourseDiscussion: {
    Icon: notificationIcon(Comment),
    Message: React.memo(function TopicAddedInCourseDiscussionNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { user } = useUser(notification.data.authorUuid)
      return (
        <Trans t={t} i18nKey="notifications.topicAddedInCourseDiscussionNotification.message">
          <UserSlug user={user} />
          <Link to={routes.course.url({ uuid: notification.data.learningItemUuid })}></Link>
          {{ author: notification.data.author }}
          {{ title: notification.data.title }}
          {{ learningItemTitle: notification.data.learningItemTitle }}
        </Trans>
      )
    }),
    Action: React.memo(function TopicAddedInCourseDiscussionNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button
          small
          component={Link}
          to={routes.courseComment.url({
            uuid: notification.data.learningItemUuid,
            commentUuid: notification.data.topicUuid,
          })}
        >
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  CommentAddedInCourseDiscussion: {
    Icon: notificationIcon(Comment),
    Message: React.memo(function CommentAddedInCourseDiscussionNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      const { user } = useUser(notification.data.authorUuid)
      return (
        <Trans t={t} i18nKey="notifications.commentAddedInCourseDiscussionNotification.message">
          <UserSlug user={user} />
          <Link to={routes.course.url({ uuid: notification.data.learningItemUuid })}></Link>
          {{ author: notification.data.author }}
          {{ learningItemTitle: notification.data.learningItemTitle }}
        </Trans>
      )
    }),
    Action: React.memo(function CommentAddedInCourseDiscussionNotificationAction({ notification }) {
      const routes = useRoutes()
      const { t } = useTranslation()
      return (
        <Button
          small
          component={Link}
          to={routes.courseComment.url({
            uuid: notification.data.learningItemUuid,
            commentUuid: notification.data.topicUuid,
          })}
        >
          {t('generic cta.view')}
        </Button>
      )
    }),
  },
  CourseRemovedFromLearningPath: {
    Icon: notificationIcon(Comment),
    Message: React.memo(function CourseRemovedFromLearningPathNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      return (
        <Trans t={t} i18nKey="notifications.courseRemovedFromLearningPathNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItemAsPath })}></Link>
          {{ title: notification.data.learningItemAsPathTitle }}
          {{ user: notification.data.userFullName }}
          {{ courses: '"' + notification.data.deletedLearningItemsTitles.join(', ') + '"' }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseRemovedFromLearningPathNotificationAction() {
      return null
    }),
  },
  CourseAddedToLearningPath: {
    Icon: notificationIcon(Comment),
    Message: React.memo(function CourseAddedFromLearningPathNotificationMessage({ notification }) {
      const { t } = useTranslation()
      const routes = useRoutes()
      return (
        <Trans t={t} i18nKey="notifications.courseAddedToLearningPathNotification.message">
          <Link to={routes.course.url({ uuid: notification.data.learningItemAsPath })}></Link>
          {{ title: notification.data.learningItemAsPathTitle }}
          {{ user: notification.data.userFullName }}
          {{ courses: '"' + notification.data.addedLearningItemsTitles.join('", "') + '"' }}
        </Trans>
      )
    }),
    Action: React.memo(function CourseAddedFromLearningPathNotificationAction() {
      return null
    }),
  },
  Unknown: {
    Icon: notificationIcon(QuestionMarkIcon),
    Message: React.memo(function UserCreatedNotificationMessage({ notification }) {
      return `Unknown notification '${notification.originalType || notification.type}'`
    }),
    Action: React.memo(function UserCreatedNotificationAction() {
      return null
    }),
  },
}

export default notificationComponents
